package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.JuridicoEmpresa
import br.com.vvrefeicoes.portal.domain.model.JuridicoEmpresaPage
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.JuridicoEmpresaRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteJuridicoEmpresaRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : JuridicoEmpresaRepository {
    override suspend fun list(
        nome: String?,
        cnpj: String?,
        tipo: String?,
        status: Boolean?,
        page: Int,
        size: Int,
    ): JuridicoEmpresaPage {
        val query =
            buildString {
                append("/juridico/empresas?page=$page&size=$size")
                nome?.takeIf(String::isNotBlank)?.let { append("&nome=${it.encodeURLParameter()}") }
                cnpj?.takeIf(String::isNotBlank)?.let { append("&cnpj=${it.encodeURLParameter()}") }
                tipo?.takeIf(String::isNotBlank)?.let { append("&tipo=${it.encodeURLParameter()}") }
                status?.let { append("&status=$it") }
            }
        return request<JuridicoEmpresaPageDto> { httpClient.get(baseUrl + query) { authorize() } }.let { response ->
            JuridicoEmpresaPage(response.content.map(JuridicoEmpresaDto::toDomain), response.page.totalPages)
        }
    }

    override suspend fun create(
        nome: String,
        cnpj: String,
        tipo: String,
    ): JuridicoEmpresa = request<JuridicoEmpresaDto> { httpClient.post(baseUrl + "/juridico/empresas") { jsonBody(JuridicoEmpresaRequest(nome, cnpj, tipo)) } }.toDomain()

    override suspend fun update(
        id: String,
        nome: String,
        cnpj: String,
        tipo: String,
    ): JuridicoEmpresa = request<JuridicoEmpresaDto> { httpClient.patch(baseUrl + "/juridico/empresas/$id") { jsonBody(JuridicoEmpresaRequest(nome, cnpj, tipo)) } }.toDomain()

    override suspend fun changeStatus(
        id: String,
        status: Boolean,
    ): JuridicoEmpresa = request<JuridicoEmpresaDto> { httpClient.patch(baseUrl + "/juridico/empresas/$id/status") { jsonBody(StatusRequest(status)) } }.toDomain()

    private suspend inline fun <reified T> request(crossinline call: suspend () -> io.ktor.client.statement.HttpResponse): T {
        val response = call()
        if (!response.status.isSuccess()) {
            val message = runCatching { Json { ignoreUnknownKeys = true }.decodeFromString<ApiError>(response.bodyAsText()).message }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value
            ?.token
            ?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }
}

@Serializable
private data class JuridicoEmpresaPageDto(
    val content: List<JuridicoEmpresaDto> = emptyList(),
    val page: JuridicoPageMetadata = JuridicoPageMetadata(),
)

@Serializable
private data class JuridicoPageMetadata(
    val totalPages: Int = 1,
)

@Serializable
private data class JuridicoEmpresaDto(
    val id: String,
    val nome: String = "",
    val cnpj: String = "",
    val tipo: String = "",
    val ativo: Boolean = true,
    val updatedAt: String? = null,
) {
    fun toDomain() = JuridicoEmpresa(id, nome, cnpj, tipo, ativo, updatedAt)
}

@Serializable
private data class JuridicoEmpresaRequest(
    val nome: String,
    val cnpj: String,
    val tipo: String,
)

@Serializable
private data class StatusRequest(
    val status: Boolean,
)

@Serializable
private data class ApiError(
    val message: String? = null,
)
